import { LockClosedIcon } from '@heroicons/react/24/outline';
import type { AppAccessStatus, AppAccessSource } from '../../services/api/appAccess';

export interface AppAccessStatusChipProps {
  status: AppAccessStatus;
  source?: AppAccessSource;
  // Optional context props for accessibility — when present, a composed aria-label is added.
  systemLabel?: string; // human-readable system name, e.g. "GitHub"
  userLabel?: string; // "{firstName} {lastName}", e.g. "Jana Novotna"
}

interface ChipConfig {
  label: string;
  className: string;
  withLockIcon?: boolean;
}

// Locked per UI-SPEC §"Status Chip: AppAccessStatusChip" — DO NOT CHANGE these class strings.
const STATUS_CONFIG: Record<AppAccessStatus, ChipConfig> = {
  granted: {
    label: 'Granted',
    className: 'bg-green-600 text-white',
  },
  assumed_granted: {
    label: 'Assumed',
    className: 'bg-transparent border border-green-600 text-green-700',
  },
  not_granted: {
    label: 'Not granted',
    className: 'bg-gray-400 text-white',
  },
  pending: {
    label: 'Pending',
    className: 'bg-blue-600 text-white animate-pulse',
  },
  failed: {
    label: 'Failed',
    className: 'bg-red-600 text-white',
  },
};

// Auto-managed sources override the status mapping: the cell is provisioned
// outside Monksheets (Slack via SSO, 1Password via SCIM) and always reads as
// granted. The label reflects the grant mechanism.
const SOURCE_CONFIG: Partial<Record<AppAccessSource, ChipConfig>> = {
  auto: {
    label: 'SSO',
    className: 'bg-green-600 text-white',
    withLockIcon: true,
  },
  scim: {
    label: 'SCIM',
    className: 'bg-green-600 text-white',
    withLockIcon: true,
  },
};

const BASE_CLASSES = 'inline-flex items-center rounded-full font-semibold px-2 py-1 text-xs';

export function AppAccessStatusChip({
  status,
  source,
  systemLabel,
  userLabel,
}: AppAccessStatusChipProps) {
  // Auto-managed source (SSO/SCIM) overrides the regular status mapping
  // (D-05 last row + UI-SPEC chip table).
  const config: ChipConfig = (source && SOURCE_CONFIG[source]) ?? STATUS_CONFIG[status];

  // Compose aria-label only when both context props are supplied (UI-SPEC §"Accessibility Contract" chip rule).
  const ariaLabel =
    systemLabel && userLabel ? `${config.label}: ${systemLabel} access for ${userLabel}` : undefined;

  return (
    <span className={`${BASE_CLASSES} ${config.className}`} aria-label={ariaLabel}>
      {config.withLockIcon && (
        <LockClosedIcon className="h-3 w-3 mr-1" aria-hidden="true" />
      )}
      {config.label}
    </span>
  );
}
